import CryptoJS from 'crypto-js';
import { useParams, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { BreadCrumbLink } from 'atoms/Breadcrumbs';
import { TKey, useTranslations } from 'modules/i18n/useTranslations';
import { usePathname } from 'modules/next/hooks';
import { useSiteConfig } from 'modules/shop/siteConfig/hooks';
import { useUser } from 'modules/shop/user/hooks';
import { PageType } from 'modules/stroyblok/types';
import {
  gtmPushPageCategory,
  gtmPushPageMeta,
  gtmPushVirtualPageView,
} from './page';
import { getRelevantCategoryPath, gtmPushRelevantVirtualUrl } from './utils';

export const useGtmPage = () => {
  const { user } = useUser();
  const { locale, ...params } = useParams();
  const pathname = usePathname();
  const { gtmData, country } = useSiteConfig();
  /** pageState need to prevent double calling events inside useEffect */
  const pageState = useRef({ hasFired: false, previousPathname: '' });
  const searchParams = useSearchParams();
  const searchParamsString = searchParams.toString();

  const isCategoryPage = Boolean(params?.category);

  useEffect(() => {
    if (locale && typeof user !== 'undefined') {
      gtmPushPageMeta({
        traffic_type: 'internal traffic',
        user_id: user?.id || '',
        language: locale as string,
        shop_country: country,
        page_type: gtmData?.pageType || '',
        login_status: !!user ? 'Logged in' : 'Logged out',
        breadcrumb: pathname.slice(1).replaceAll('/', '|'),
        user_type: user?.customData?.company ? 'company' : 'private customer',
        wt_customerid: !!user?.email
          ? CryptoJS.MD5(user.email.trim()).toString()
          : '',
        /** @todo add newsletter_subscriber when the newsletter service is connected */
        // newsletter_subscriber: user?.newsletter
        //   ? 'newsletter subscriber'
        //   : 'no subscriber',
      });

      gtmPushVirtualPageView({
        path: pathname,
        /** @todo add real data */
        // title: 'page title',
      });
    }

    /** prevent calling events few times but call them when url changes */
    if (pathname !== pageState.current.previousPathname) {
      pageState.current.hasFired = false;

      if (!pageState.current.hasFired) {
        gtmPushRelevantVirtualUrl(
          pathname,
          isCategoryPage ? searchParamsString : '',
        );

        pageState.current.hasFired = true;
      }

      pageState.current.previousPathname = pathname;
    }
  }, [pathname, user]);
};

export const useGtmCategoryPage = ({
  breadcrumbs,
  pageType,
  nonStaticPage,
  gtm,
}: {
  breadcrumbs?: BreadCrumbLink[];
  pageType?: PageType;
  nonStaticPage?: boolean;
  gtm: {
    gtmPushPageCategory: typeof gtmPushPageCategory;
  };
}) => {
  const t = useTranslations();
  const home = t('breadcrumbs.home');
  const pathname = usePathname();
  const lastPathname = useRef('');

  useEffect(() => {
    if (breadcrumbs && lastPathname.current !== pathname) {
      const breadcrumbsTitles = breadcrumbs.map(({ title }) =>
        nonStaticPage ? title : t(`breadcrumbs.${title}` as TKey),
      );

      const path = `${home} > ${getRelevantCategoryPath(breadcrumbsTitles, pathname, pageType)}`;

      gtm.gtmPushPageCategory(path);

      lastPathname.current = pathname;
    }
  }, [pathname]);
};
